<template>
  <v-card>
    <loader v-if="fetchCompanyPending || !dataLoaded"/>
    <template v-else>
      <v-card-text class="pa-5">
        <v-container class="pa-0" grid-list-md>
          <v-row>
            <v-col :cols="12">
              <h2>{{ $t('texts.logo') }}</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <vue-dropzone
                ref="companyLogoDropzone"
                :id="'companyLogoDropzone' + selectedCompanyId"
                @vdropzone-mounted="companyLogoDropzoneMounted"
                @vdropzone-success="companyLogoDropzoneSuccess"
                @vdropzone-file-added="updateHeader"
                :options="companyLogoDropzoneOptions"
              />
              <p>
                {{ $t('texts.company.profile.images-type-info') }}
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col :cols="12">
              <h2>{{ $t('texts.company.image') }}</h2>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <vue-dropzone
                ref="companyImageDropzone"
                :id="'companyImageDropzone' + selectedCompanyId"
                @vdropzone-mounted="companyImageDropzoneMounted"
                @vdropzone-sending="companyImageDropzoneSending"
                @vdropzone-removed-file="companyImageDropzoneRemoveFile"
                @vdropzone-success="companyImageDropzoneSuccess"
                @vdropzone-file-added="updateHeader"
                :options="companyImageDropzoneOptions"
                :destroyDropzone="false"
              >
                <input type="hidden" name="companyId" :value="selectedCompanyId">
              </vue-dropzone>
              <p>
                {{ $t('texts.company.profile.images-size-info') }}<br>
                {{ $t('texts.company.profile.images-type-info') }}
              </p>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <template v-if="!company.active_subscription">
        <v-divider/>
        <v-card-actions class="px-5 py-3">
          <cta-buy-subscription/>
        </v-card-actions>
      </template>
    </template>
  </v-card>
</template>

<script>
import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';
import axios from 'axios';
import vue2Dropzone from 'vue2-dropzone';
import { apiDomain } from '@/config';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import CtaBuySubscription from '@/components/_molecules/company/CTABuySubscription.vue';

const { mapState: mapCompanyProfileState, mapActions: mapCompanyProfileActions } = createNamespacedHelpers('company/profile');

export default {
  name: 'CompanyProfile',
  components: {
    vueDropzone: vue2Dropzone,
    CtaBuySubscription,
  },
  data() {
    return {
      dataLoaded: false,
      form: {
        name: '',
        logo: '',
        images: {},
      },
      customImageMaxSize: 5, // megabytes
    };
  },
  created() {
    this.fetchCompany(this.selectedCompanyId).then(() => {
      Object.assign(this.form, this.company);
      this.dataLoaded = true;
    });
  },
  computed: {
    ...mapCompanyProfileState([
      'fetchCompanyPending',
      'storeCompanyPending',
      'company',
    ]),
    companyLogoDropzoneOptions() {
      return {
        acceptedFiles: '.jpg,.jpeg,.png,.gif',
        url: `${apiDomain}company/${this.selectedCompanyId}/logo`,
        paramName: 'logo',
        thumbnailMethod: 'contain',
        maxFileSizeInMB: 5,
        maxNumberOfFiles: 2,
        maxFiles: 2,
        parallelUploads: 1,
        addRemoveLinks: false,
        resizeWidth: 800,
        resizeQuality: 0.8,
        thumbnailWidth: null,
        thumbnailHeight: null,
        dictDefaultMessage: this.$i18n.t('texts.imageUploadInfo'),
      };
    },
    companyImageDropzoneOptions() {
      return {
        acceptedFiles: '.jpg,.jpeg,.png,.gif',
        url: `${apiDomain}company/${this.selectedCompanyId}/image`,
        paramName: 'image',
        thumbnailMethod: 'contain',
        maxFilesize: 5,
        maxNumberOfFiles: 2,
        maxFiles: 2,
        parallelUploads: 1,
        addRemoveLinks: false,
        resizeWidth: 800,
        resizeQuality: 0.8,
        thumbnailWidth: null,
        thumbnailHeight: null,
        dictDefaultMessage: this.$i18n.t('texts.imageUploadInfo'),
      };
    },
    selectedCompanyId() {
      return this.$route.params.companyId;
    },
  },
  methods: {
    ...mapCompanyProfileActions([
      'fetchCompany',
      'storeCompany',
      'checkCompanyProfile',
    ]),
    companyLogoDropzoneMounted() {
      if (this.form.logo) {
        Vue.nextTick(() => {
          const file = {
            size: 1,
            name: '',
            type: 'image',
          };
          const options = {
            dontSubstractMaxFiles: true,
            addToFiles: false,
          };
          this.$refs.companyLogoDropzone.manuallyAddFile(file, this.form.logo, null, null, options);
        });
      }
    },
    companyImageDropzoneMounted() {
      if (this.form.images && this.form.images.length > 0) {
        Vue.nextTick(() => {
          this.form.images.forEach((item) => {
            const file = {
              size: 1,
              name: '',
              type: 'image',
            };
            const options = {
              dontSubstractMaxFiles: true,
              addToFiles: false,
            };
            this.$refs.companyImageDropzone.manuallyAddFile(file, item.path, null, null, options);
          });
        });
      }
    },
    companyImageDropzoneSending(file, xhr, formData) {
      formData.append('companyId', this.selectedCompanyId);
    },
    companyImageDropzoneRemoveFile() {
      return true;
    },
    showSuccess() {
      this.checkCompanyProfile({
        companyId: this.selectedCompanyId,
      });
    },
    companyLogoDropzoneSuccess() {
      // Check how many logos are currently added
      const files = { ...this.$refs.companyLogoDropzone.dropzone.files };
      const numberOfFiles = Object.keys(files).length;
      if (numberOfFiles > 1) {
        this.$refs.companyLogoDropzone.removeFile(files[0]);
      }
      this.showSuccess();
    },
    companyImageDropzoneSuccess(file, response) {
      file.id = response.id;
      // Check how many images are currently added
      const files = { ...this.$refs.companyImageDropzone.dropzone.files };
      const numberOfFiles = Object.keys(files).length;
      if (numberOfFiles > 1) {
        this.$refs.companyImageDropzone.removeFile(files[0]);
      }
      this.showSuccess();
    },
    updateHeader() {
      this.$refs.companyLogoDropzone.setOption('headers', { Authorization: `Bearer ${this.$auth.token()}` });
      this.$refs.companyImageDropzone.setOption('headers', { Authorization: `Bearer ${this.$auth.token()}` });
    },
  },
};
</script>
